import React from "react";
import { Link } from "react-router-dom";

const RulesPage = () => {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <Link to="/home" className="text-deep-red hover:text-bright-red">
          &larr; Back to Home
        </Link>
      </div>

      <h1 className="text-3xl font-bold text-dark-grey mb-8">How to Play</h1>

      <div className="space-y-10">
        {/* Pick 5 Fighters */}
        <section>
          <h2 className="text-2xl font-semibold text-dark-grey mb-4">
            1. Pick 5 Fighters
          </h2>
          <p className="text-medium-grey mb-3">
            For every event, select 5 fighters from the fight card.
          </p>
          <ul className="list-disc pl-5 text-medium-grey space-y-2">
            <li>You cannot pick fighters competing against each other.</li>
            <li>Picks must be submitted before the first fight starts.</li>
            <li>You can edit your lineup anytime before the deadline.</li>
            <li>No late changes or exceptions.</li>
          </ul>
        </section>

        {/* Rank Your Fighters */}
        <section>
          <h2 className="text-2xl font-semibold text-dark-grey mb-4">
            2. Rank Your Fighters (1–5)
          </h2>
          <p className="text-medium-grey mb-4">
            After choosing your fighters, rank them from 1 to 5. Your ranking
            determines how many points you can earn:
          </p>
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full bg-white border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-4 border-b text-left">Rank</th>
                  <th className="py-2 px-4 border-b text-left">
                    Points Earned
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2 px-4 border-b font-semibold">#1</td>
                  <td className="py-2 px-4 border-b">100%</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border-b font-semibold">#2</td>
                  <td className="py-2 px-4 border-b">80%</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border-b font-semibold">#3</td>
                  <td className="py-2 px-4 border-b">60%</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border-b font-semibold">#4</td>
                  <td className="py-2 px-4 border-b">40%</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border-b font-semibold">#5</td>
                  <td className="py-2 px-4 border-b">20%</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-dark-grey mb-2">Example</h4>
            <p className="text-medium-grey text-sm">
              If a fighter's projected score is 12.05 points:
            </p>
            <ul className="list-disc pl-5 text-medium-grey text-sm space-y-1 mt-2">
              <li>Rank #1 = 12.05</li>
              <li>Rank #2 = 9.64</li>
              <li>Rank #3 = 7.23</li>
              <li>Rank #4 = 4.82</li>
              <li>Rank #5 = 2.41</li>
            </ul>
            <p className="text-medium-grey text-sm mt-2 italic">
              The higher you rank a fighter, the more points you can earn.
            </p>
          </div>
        </section>

        {/* Scoring System */}
        <section>
          <h2 className="text-2xl font-semibold text-dark-grey mb-4">
            Scoring System
          </h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-dark-grey mb-2">
                How You Earn Points
              </h3>
              <p className="text-medium-grey">
                You score points when your selected fighters win. Projected
                fighter points are released every Tuesday at 12:00 PM ET before
                the event.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-dark-grey mb-2">
                How You Lose Points
              </h3>
              <p className="text-medium-grey mb-2">
                There are only 2 ways to lose points:
              </p>
              <ol className="list-decimal pl-5 text-medium-grey space-y-1">
                <li>Your Lock of the Night loses</li>
                <li>A fighter you selected gets finished/stopped</li>
              </ol>
              <p className="text-medium-grey mt-2 text-sm italic">
                A decision loss does not cost points unless that fighter was
                your LOTN.
              </p>
            </div>
          </div>
        </section>

        {/* Lock of the Night */}
        <section>
          <h2 className="text-2xl font-semibold text-dark-grey mb-4">
            Lock of the Night (LOTN)
          </h2>
          <p className="text-medium-grey mb-4">
            Choose 1 fighter as your Lock of the Night. Your LOTN will either:
          </p>
          <ul className="list-disc pl-5 text-medium-grey space-y-2 mb-4">
            <li>Earn DOUBLE ranking points if they win</li>
            <li>Lose DOUBLE ranking points if they lose</li>
          </ul>

          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-dark-grey mb-2">Example</h4>
            <p className="text-medium-grey text-sm mb-2">
              A fighter worth 12.05 points:
            </p>
            <ul className="list-disc pl-5 text-medium-grey text-sm space-y-1">
              <li>
                <span className="text-green-600 font-medium">LOTN Win:</span>{" "}
                12.05 × 2 = +24.1
              </li>
              <li>
                <span className="text-red-600 font-medium">LOTN Loss:</span>{" "}
                12.05 × 2 = -24.1
              </li>
            </ul>
          </div>

          <p className="text-medium-grey text-sm bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <strong>Important:</strong> Only ranking points are doubled.
            Finish/Stoppage bonus points are not doubled.
          </p>
        </section>

        {/* Finish / Stoppage Bonus */}
        <section>
          <h2 className="text-2xl font-semibold text-dark-grey mb-4">
            Finish / Stoppage Bonus
          </h2>
          <p className="text-medium-grey mb-4">A stoppage includes:</p>
          <ul className="list-disc pl-5 text-medium-grey space-y-1 mb-6">
            <li>KO</li>
            <li>TKO</li>
            <li>Submission</li>
            <li>DQ</li>
          </ul>

          <h3 className="text-lg font-semibold text-dark-grey mb-3">
            3-Round Fight Bonuses
          </h3>
          <div className="overflow-x-auto mb-6">
            <table className="min-w-full bg-white border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-4 border-b text-left">Finish Round</th>
                  <th className="py-2 px-4 border-b text-left">Bonus</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2 px-4 border-b">Round 1</td>
                  <td className="py-2 px-4 border-b">+3 / -3</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border-b">Round 2</td>
                  <td className="py-2 px-4 border-b">+2 / -2</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border-b">Round 3</td>
                  <td className="py-2 px-4 border-b">+1 / -1</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-lg font-semibold text-dark-grey mb-3">
            5-Round Fight Bonuses
          </h3>
          <div className="overflow-x-auto mb-6">
            <table className="min-w-full bg-white border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-4 border-b text-left">Finish Round</th>
                  <th className="py-2 px-4 border-b text-left">Bonus</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2 px-4 border-b">Round 1</td>
                  <td className="py-2 px-4 border-b">+5 / -5</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border-b">Round 2</td>
                  <td className="py-2 px-4 border-b">+4 / -4</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border-b">Round 3</td>
                  <td className="py-2 px-4 border-b">+3 / -3</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border-b">Round 4</td>
                  <td className="py-2 px-4 border-b">+4 / -4</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border-b">Round 5</td>
                  <td className="py-2 px-4 border-b">+5 / -5</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-dark-grey mb-2">
              Decision Results
            </h4>
            <ul className="list-disc pl-5 text-medium-grey text-sm space-y-1">
              <li>Win by decision = 0 bonus</li>
              <li>Lose by decision = 0 penalty</li>
              <li>Unless the fighter is your LOTN</li>
            </ul>
          </div>
        </section>

        {/* Draws & No Contests */}
        <section>
          <h2 className="text-2xl font-semibold text-dark-grey mb-4">
            Draws & No Contests
          </h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-dark-grey mb-2">
                Draws
              </h3>
              <p className="text-medium-grey mb-2">
                If your fighter's bout ends in a draw, you receive half of the
                projected ranking points.
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-medium-grey text-sm">
                  <strong>Example:</strong> Projected points = 6.10
                </p>
                <ul className="list-disc pl-5 text-medium-grey text-sm space-y-1 mt-1">
                  <li>Draw = 3.05 points</li>
                  <li>If LOTN = 6.10 points</li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-dark-grey mb-2">
                No Contest
              </h3>
              <p className="text-medium-grey">
                If a fight becomes a No Contest, your alternate fighter will
                replace that selection.
              </p>
            </div>
          </div>
        </section>

        {/* Alternate Fighters */}
        <section>
          <h2 className="text-2xl font-semibold text-dark-grey mb-4">
            Alternate Fighters
          </h2>
          <p className="text-medium-grey mb-3">You can select:</p>
          <ul className="list-disc pl-5 text-medium-grey space-y-1 mb-4">
            <li>Up to 2 alternate fighters</li>
            <li>An Alternate LOTN</li>
          </ul>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-dark-grey mb-2">
                If Your Fighter Is Removed
              </h3>
              <p className="text-medium-grey">Your alternate fighter:</p>
              <ul className="list-disc pl-5 text-medium-grey space-y-1 mt-1">
                <li>Moves into your #5 slot</li>
                <li>All other fighters move up one position</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-dark-grey mb-2">
                If You Do Not Choose Alternates
              </h3>
              <p className="text-medium-grey">
                The system will automatically assign:
              </p>
              <ul className="list-disc pl-5 text-medium-grey space-y-1 mt-1">
                <li>The best available favorite</li>
                <li>
                  A valid fighter not already conflicting with your lineup
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Auto-Selection Rules */}
        <section>
          <h2 className="text-2xl font-semibold text-dark-grey mb-4">
            Auto-Selection Rules
          </h2>
          <p className="text-medium-grey mb-3">
            If you miss the lineup deadline:
          </p>
          <ul className="list-disc pl-5 text-medium-grey space-y-1 mb-4">
            <li>The system auto-selects the top 5 betting favorites</li>
            <li>Tiebreakers favor fighters closest to the main event</li>
          </ul>

          <div className="space-y-3">
            <div>
              <h4 className="font-semibold text-dark-grey">
                Auto-Selected LOTN
              </h4>
              <p className="text-medium-grey text-sm">
                Your LOTN becomes the biggest favorite on the card.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-dark-grey">
                Auto-Selection Penalty
              </h4>
              <p className="text-medium-grey text-sm">
                League commissioners may apply a scoring penalty for
                auto-selected lineups.
              </p>
            </div>
          </div>
        </section>

        {/* Standings & Results */}
        <section>
          <h2 className="text-2xl font-semibold text-dark-grey mb-4">
            Standings & Results
          </h2>
          <ul className="list-disc pl-5 text-medium-grey space-y-2">
            <li>
              Event scores are finalized and posted by Monday at 11:59 PM ET
            </li>
            <li>Standings are updated after every event</li>
          </ul>
        </section>

        {/* Global Competition */}
        <section>
          <h2 className="text-2xl font-semibold text-dark-grey mb-4">
            Global Competition
          </h2>
          <p className="text-medium-grey mb-4">
            The Global Competition is a continuous worldwide leaderboard.
          </p>

          <div className="space-y-3">
            <div>
              <h3 className="text-lg font-semibold text-dark-grey mb-2">
                How It Works
              </h3>
              <ul className="list-disc pl-5 text-medium-grey space-y-1">
                <li>Earn points to increase your global status tier</li>
                <li>Compete against all league members worldwide</li>
                <li>Higher cumulative points unlock higher ranks</li>
              </ul>
            </div>

            <p className="text-medium-grey text-sm bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <strong>Important:</strong> Global points cannot go below zero.
              Your Global status tier can drop if your points fall below the
              required threshold.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default RulesPage;
